import {
  SAFETY_CATEGORIES,
  SAFETY_CATEGORY_LABELS,
  type SafetyCategory,
  type SafetyKindItem,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Dialog } from '@/components/dialog';
import { FormError, SafetyNav } from '@/components/safety-bits';
import { Button, ErrorBox, Input, PageHeader, Select, Spinner } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/lib/auth';
import { plural } from '@/lib/format';
import { safetyMetaQuery } from '@/lib/safety';

interface Draft {
  key: string;
  id?: string;
  name: string;
  category: SafetyCategory;
  periodMonths: string;
  remindDays: string;
  appliesToAll: boolean;
  positionIds: string[];
  requiresSignature: boolean;
  reasonRequired: boolean;
  isActive: boolean;
}

const toDraft = (k: SafetyKindItem): Draft => ({
  key: k.id,
  id: k.id,
  name: k.name,
  category: k.category,
  periodMonths: k.periodMonths ? String(k.periodMonths) : '',
  remindDays: String(k.remindDays),
  appliesToAll: k.appliesToAll,
  positionIds: k.positionIds,
  requiresSignature: k.requiresSignature,
  reasonRequired: k.reasonRequired,
  isActive: k.isActive,
});

export function SafetySettingsPage() {
  const can = useCan();
  const meta = useQuery(safetyMetaQuery);
  if (!can('safety.settings.manage'))
    return <ErrorBox error={new Error('Настройки доступны ответственному по охране труда')} />;
  if (meta.isPending) return <Spinner />;
  if (meta.error) return <ErrorBox error={meta.error} />;
  return (
    <div>
      <PageHeader
        title="Настройки охраны труда"
        description="Виды инструктажей, обучения и медосмотров, их периодичность и кому они обязательны"
      />
      <SafetyNav />
      <KindsEditor key={meta.dataUpdatedAt} />
    </div>
  );
}

function KindsEditor() {
  const qc = useQueryClient();
  const meta = useQuery(safetyMetaQuery);
  const positions = meta.data?.positions ?? [];
  const [rows, setRows] = useState<Draft[]>(() => (meta.data?.kinds ?? []).map(toDraft));
  const [picking, setPicking] = useState<Draft | null>(null);
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api('/safety/settings/kinds', {
        method: 'PUT',
        body: {
          kinds: rows.map((r) => ({
            id: r.id,
            name: r.name.trim(),
            category: r.category,
            periodMonths: r.periodMonths.trim() ? Number(r.periodMonths) : null,
            remindDays: Number(r.remindDays || 0),
            appliesToAll: r.appliesToAll,
            positionIds: r.appliesToAll ? [] : r.positionIds,
            requiresSignature: r.requiresSignature,
            reasonRequired: r.reasonRequired,
            isActive: r.isActive,
          })),
        },
      }),
    onSuccess: async () => {
      setSaved(true);
      await qc.invalidateQueries({ queryKey: ['safety'] });
    },
  });
  const patch = (key: string, p: Partial<Draft>) => {
    setSaved(false);
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r)));
  };
  /** Сдвиг внутри своего раздела: меняемся местами с соседом той же категории. */
  const neighbour = (i: number, d: -1 | 1) => {
    const cat = rows[i]?.category;
    for (let j = i + d; j >= 0 && j < rows.length; j += d) if (rows[j]?.category === cat) return j;
    return -1;
  };
  const move = (i: number, d: -1 | 1) => {
    const j = neighbour(i, d);
    if (j < 0) return;
    setSaved(false);
    setRows((rs) => {
      const next = [...rs];
      [next[i], next[j]] = [next[j] as Draft, next[i] as Draft];
      return next;
    });
  };
  const errorText =
    save.error instanceof ApiError
      ? (Object.values(save.error.fields)[0] ?? save.error.message)
      : save.error?.message;

  return (
    <div className="flex max-w-[1080px] flex-col gap-4">
      <p className="max-w-[80ch] text-[13.5px] text-steel">
        Периодичность пустая — разовый (вводный, предварительный медосмотр). «Кому» — всем сотрудникам или
        выбранным должностям; у конкретного человека можно отметить «не требуется» в его карточке. Вид, по
        которому уже есть записи, при удалении выключается. Новая периодичность действует для новых записей.
      </p>
      {SAFETY_CATEGORIES.map((cat) => {
        const list = rows.map((r, i) => ({ r, i })).filter((x) => x.r.category === cat);
        return (
          <section key={cat} className="panel">
            <div className="flex items-center justify-between border-b border-line px-4 py-3">
              <h2 className="text-[15px] font-semibold">{SAFETY_CATEGORY_LABELS[cat]}</h2>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setSaved(false);
                  setRows((rs) => [
                    ...rs,
                    {
                      key: crypto.randomUUID(),
                      name: '',
                      category: cat,
                      periodMonths: cat === 'medical' ? '12' : cat === 'training' ? '36' : '',
                      remindDays: cat === 'instruction' ? '14' : '30',
                      appliesToAll: false,
                      positionIds: [],
                      requiresSignature: cat !== 'medical',
                      reasonRequired: false,
                      isActive: true,
                    },
                  ]);
                }}
              >
                <Plus className="size-4" aria-hidden />
                Вид
              </Button>
            </div>
            <ul className="divide-y divide-line">
              {list.map(({ r, i }) => (
                <li key={r.key} className="grid gap-2 px-4 py-3 lg:grid-cols-[auto_minmax(0,1fr)] lg:gap-x-6">
                  <Input
                    aria-label="Название"
                    value={r.name}
                    onChange={(e) => patch(r.key, { name: e.target.value })}
                    placeholder="Название"
                    className="lg:col-span-2 lg:max-w-[640px]"
                  />
                  <div className="flex flex-wrap items-center gap-2 text-[13px]">
                    <span className="inline-flex items-center gap-1.5">
                      раз в
                      <Input
                        aria-label="Периодичность, месяцев"
                        inputMode="numeric"
                        value={r.periodMonths}
                        onChange={(e) => patch(r.key, { periodMonths: e.target.value.replace(/\D/g, '') })}
                        placeholder="—"
                        className="w-14 text-center"
                      />
                      мес.
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      напомнить за
                      <Input
                        aria-label="Напомнить за, дней"
                        inputMode="numeric"
                        value={r.remindDays}
                        onChange={(e) => patch(r.key, { remindDays: e.target.value.replace(/\D/g, '') })}
                        className="w-14 text-center"
                      />
                      дн.
                    </span>
                    <Select
                      aria-label="Кому обязателен"
                      value={r.appliesToAll ? 'all' : 'positions'}
                      onChange={(e) => patch(r.key, { appliesToAll: e.target.value === 'all' })}
                      className="w-[150px]"
                    >
                      <option value="all">Всем</option>
                      <option value="positions">Должностям</option>
                    </Select>
                    {!r.appliesToAll && (
                      <button type="button" className="link" onClick={() => setPicking(r)}>
                        {r.positionIds.length
                          ? `${r.positionIds.length} ${plural(r.positionIds.length, ['должность', 'должности', 'должностей'])}`
                          : 'никому (по случаю)'}
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-[13px]">
                    <label className="inline-flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        className="size-4 accent-graphite"
                        checked={r.requiresSignature}
                        onChange={(e) => patch(r.key, { requiresSignature: e.target.checked })}
                      />
                      подпись
                    </label>
                    <label className="inline-flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        className="size-4 accent-graphite"
                        checked={r.reasonRequired}
                        onChange={(e) => patch(r.key, { reasonRequired: e.target.checked })}
                      />
                      причина
                    </label>
                    <label className="inline-flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        className="size-4 accent-graphite"
                        checked={r.isActive}
                        onChange={(e) => patch(r.key, { isActive: e.target.checked })}
                      />
                      включён
                    </label>
                    <span className="ml-auto inline-flex gap-0.5">
                      <button
                        type="button"
                        aria-label="Выше"
                        disabled={neighbour(i, -1) < 0}
                        onClick={() => move(i, -1)}
                        className="inline-flex size-8 items-center justify-center rounded text-steel hover:bg-sunken disabled:opacity-30"
                      >
                        <ArrowUp className="size-4" />
                      </button>
                      <button
                        type="button"
                        aria-label="Ниже"
                        disabled={neighbour(i, 1) < 0}
                        onClick={() => move(i, 1)}
                        className="inline-flex size-8 items-center justify-center rounded text-steel hover:bg-sunken disabled:opacity-30"
                      >
                        <ArrowDown className="size-4" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Удалить ${r.name}`}
                        onClick={() => {
                          setSaved(false);
                          setRows((rs) => rs.filter((x) => x.key !== r.key));
                        }}
                        className="inline-flex size-8 items-center justify-center rounded text-steel hover:bg-danger-soft hover:text-danger"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </span>
                  </div>
                </li>
              ))}
              {list.length === 0 && <li className="px-4 py-3 text-steel">Нет видов</li>}
            </ul>
          </section>
        );
      })}
      {save.error && <FormError>{errorText}</FormError>}
      <div className="flex items-center gap-3">
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          Сохранить
        </Button>
        {saved && <span className="text-[13.5px] text-ok">Сохранено</span>}
      </div>
      {picking && (
        <PositionsDialog
          draft={picking}
          positions={positions}
          onClose={() => setPicking(null)}
          onSave={(ids) => {
            patch(picking.key, { positionIds: ids });
            setPicking(null);
          }}
        />
      )}
    </div>
  );
}

function PositionsDialog({
  draft,
  positions,
  onClose,
  onSave,
}: {
  draft: Draft;
  positions: { id: string; name: string }[];
  onClose: () => void;
  onSave: (ids: string[]) => void;
}) {
  const [ids, setIds] = useState<string[]>(draft.positionIds);
  const [q, setQ] = useState('');
  const shown = positions.filter((p) => p.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <Dialog title={draft.name || 'Должности'} onClose={onClose} width={520}>
      <div className="flex flex-col gap-3 px-5 py-5">
        <p className="text-[13.5px] text-steel">
          Кому вид обязателен. Для остальных его можно внести по случаю.
        </p>
        <Input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск должности" />
        <ul className="max-h-[50vh] overflow-y-auto">
          {shown.map((p) => (
            <li key={p.id}>
              <label className="flex items-center gap-2 rounded px-1 py-1.5 hover:bg-sunken">
                <input
                  type="checkbox"
                  className="size-4 accent-graphite"
                  checked={ids.includes(p.id)}
                  onChange={(e) =>
                    setIds((x) => (e.target.checked ? [...x, p.id] : x.filter((v) => v !== p.id)))
                  }
                />
                {p.name}
              </label>
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={() => onSave(ids)}>Готово ({ids.length})</Button>
        </div>
      </div>
    </Dialog>
  );
}
